// The bottom tab bar, and the sidebar it becomes on a wide screen.
//
// ONE ELEMENT, TWO SHAPES. The prototype draws both from a single `<nav>` and a
// container query, and this is that ported rather than translated: at phone
// width it is a row of four icon-over-label buttons across the bottom; at 900px
// and over `.app` turns into a row, the nav takes `order: -1` and 236px, and the
// brand, the add action and the Settings entry - all `display: none` below that
// - appear (prototype-v1.md section 6, `screenshots/desktop-todayA.png`).
//
// Two of those are worth naming, because each closes a hole rather than being
// decoration:
//
//   THE ADD ACTION IS THE SIDEBAR'S, and the floating + is the phone's. A
//   corner-anchored circle on a 1440px window is a phone idiom in the wrong
//   place, which is why the approved design replaces it here - and why `.fab`
//   goes back to being hidden at this width now that there is something to
//   replace it with. Until this landed it had to stay at every width, or a wide
//   screen had no way to add anything at all (web/src/README.md).
//
//   SETTINGS IS NOT A TAB. It sits at the foot of the sidebar, and on a phone it
//   is reached from the account button in each screen's top row instead. Both
//   are the approved design; keeping it off the tab bar is what keeps the daily
//   path to the four screens a person actually plans in.

import { BrandMark, Icon } from '../day/Icon.tsx';
import { TABS } from './screens.ts';
import type { ScreenName } from './screens.ts';

interface Props {
  screen: ScreenName;
  onGo: (screen: ScreenName) => void;
  /** Opens the add flow. The sidebar's only action; see the note above. */
  onAdd: () => void;
  /** False while no day is known, which is also while nothing can be added. */
  canAdd: boolean;
  /**
   * A sheet is open. The whole nav goes with the screen behind the scrim: a
   * z-index says nothing to a keyboard, and without this the first Tab out of a
   * dialog lands on a tab bar drawn underneath it (`App.tsx`).
   */
  inert: boolean;
}

export function NavBar({ screen, onGo, onAdd, canAdd, inert }: Props) {
  // The `<nav>` is NAMED, because the day screen has a pill called `Today` and
  // this has a tab called `Today`: what tells the two apart, for a screen reader
  // as much as for a test, is the group each is in - this landmark, and the
  // chooser's `role="group"` called "Choose a day".
  return (
    <nav className="nav" aria-label="Screens" inert={inert}>
      {/* Sidebar only. The day screen drops its own wordmark at the same width,
          so exactly one of the two is ever on screen. */}
      <div className="brand">
        <BrandMark />
        <span className="brand-name">Perfect Note</span>
      </div>

      {/* Disabled rather than hidden while the day is on its way, for the same
          reason the floating button is: a control that comes and goes as a
          screen loads is a moving target. */}
      <button className="navnew" disabled={!canAdd} onClick={onAdd}>
        <Icon name="plus" />
        <span>Add something</span>
      </button>

      {TABS.map((tab) => (
        <button
          key={tab.key}
          // `aria-current="page"` rather than `aria-selected`: these are links
          // between screens, not tabs inside one panel, and the approved design
          // marks them exactly this way.
          aria-current={screen === tab.key ? 'page' : undefined}
          onClick={() => onGo(tab.key)}
        >
          <Icon name={tab.icon} />
          <span>{tab.label}</span>
        </button>
      ))}

      <div className="navfoot">
        <button
          aria-current={screen === 'settings' ? 'page' : undefined}
          onClick={() => onGo('settings')}
        >
          <Icon name="gear" />
          <span>Settings</span>
        </button>
      </div>
    </nav>
  );
}

/**
 * The account button in a screen's top row: the way to Settings on a phone.
 *
 * The approved design draws an avatar with the account's initial in it
 * (`screenshots/phone-settings.png`). There is no endpoint that says who is
 * signed in - `GET /api/v1/me` does not exist - so this carries the settings
 * glyph rather than an invented letter, in the avatar's own shape and place.
 * Whoever adds that endpoint swaps the glyph for the initial and nothing else
 * moves.
 *
 * It hides at sidebar width, where the sidebar's own Settings entry answers for
 * it, so the two are never both on screen.
 */
export function AccountButton({ onClick }: { onClick: () => void }) {
  return (
    <button className="avatar" aria-label="Settings" onClick={onClick}>
      <Icon name="gear" />
    </button>
  );
}
